import React, { useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Stack, Paper, Divider, Chip, Alert, useTheme,
  TextField, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TableChartIcon from '@mui/icons-material/TableChart';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SendIcon from '@mui/icons-material/Send';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useNavigate } from 'react-router-dom';
import MapView from './MapView';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import {
  readFileAsText, csvToGeoJSON, parseCSVCoordinates,
  detectSeparator, autoDetectColumns, exportGeoJSON,
} from '../../utils/geoUtils';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';
import L from 'leaflet';

const ROLE_COLORS = {
  lat: '#10b981',
  lon: '#3b82f6',
  time: '#f59e0b',
  speed: '#ef4444',
  heading: '#8b5cf6',
  altitude: '#06b6d4',
};

const ROLE_LABELS = {
  lat: 'Latitude',
  lon: 'Longitude',
  time: 'Timestamp',
  speed: 'Speed',
  heading: 'Heading',
  altitude: 'Altitude',
};

function CsvToGeoJson() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [csvText, setCsvText] = useState('');
  const [error, setError] = useState('');
  const [geometryType, setGeometryType] = useState('points');
  const [showPaste, setShowPaste] = useState(true);
  const [copyFeedback, setCopyFeedback] = useState('');

  // Column override state
  const [colOverrides, setColOverrides] = useState({});

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      setCsvText(text);
      setColOverrides({}); // Reset overrides on new file
      setError('');
      setShowPaste(false);
    } catch (err) {
      setError(`Failed to read file: ${err.message}`);
    }
  }, []);

  // Parse & detect
  const parseResult = useMemo(() => {
    if (!csvText.trim()) return null;
    try {
      const { separator, separatorName } = detectSeparator(csvText);
      const lines = csvText.trim().split('\n');
      const allRows = lines.map(l => l.trim().split(separator).map(p => p.trim()));
      const firstRow = allRows[0];
      const numericInFirst = firstRow.filter(v => !isNaN(parseFloat(v)) && v.trim() !== '').length;
      const hasHeaderRow = numericInFirst < firstRow.length * 0.5;
      const headers = hasHeaderRow ? allRows[0] : allRows[0].map((_, i) => `col_${i}`);
      const dataRows = hasHeaderRow ? allRows.slice(1) : allRows;
      const sampleRows = dataRows.slice(0, 20);
      const detected = autoDetectColumns(headers, sampleRows);

      return {
        headers,
        dataRows,
        sampleRows: dataRows.slice(0, 5),
        hasHeaderRow,
        separatorName,
        detected,
        totalRows: dataRows.length,
        colCount: headers.length,
      };
    } catch (err) {
      setError(`Parse error: ${err.message}`);
      return null;
    }
  }, [csvText]);

  // Effective column assignments (detected + overrides)
  const effectiveCols = useMemo(() => {
    if (!parseResult) return {};
    const d = parseResult.detected;
    return {
      latIdx: colOverrides.latIdx ?? d.latIdx,
      lonIdx: colOverrides.lonIdx ?? d.lonIdx,
      timeIdx: colOverrides.timeIdx ?? d.timeIdx,
      speedIdx: colOverrides.speedIdx ?? d.speedIdx,
    };
  }, [parseResult, colOverrides]);

  // Convert to GeoJSON
  const conversionResult = useMemo(() => {
    if (!csvText.trim()) return null;
    try {
      const result = csvToGeoJSON(csvText, {
        geometryType,
        overrides: effectiveCols,
      });
      if (!result.geoJson) {
        setError('Could not detect lat/lon columns. Please assign them manually.');
      } else {
        setError('');
      }
      return result;
    } catch (err) {
      setError(`Conversion error: ${err.message}`);
      return null;
    }
  }, [csvText, geometryType, effectiveCols]);

  const getColRole = (colIdx) => {
    if (colIdx === effectiveCols.latIdx) return 'lat';
    if (colIdx === effectiveCols.lonIdx) return 'lon';
    if (colIdx === effectiveCols.timeIdx) return 'time';
    if (colIdx === effectiveCols.speedIdx) return 'speed';
    return null;
  };

  const handleColRoleChange = (colIdx, role) => {
    const newOverrides = { ...colOverrides };
    // Remove this column from any existing role
    ['latIdx', 'lonIdx', 'timeIdx', 'speedIdx'].forEach(key => {
      if (newOverrides[key] === colIdx) delete newOverrides[key];
    });
    // Assign new role
    if (role) {
      const roleKey = `${role}Idx`;
      newOverrides[roleKey] = colIdx;
    }
    setColOverrides(newOverrides);
  };

  const handleCopy = useCallback((text) => {
    navigator.clipboard.writeText(text);
    setCopyFeedback('Copied!');
    setTimeout(() => setCopyFeedback(''), 1500);
  }, []);

  const pointToLayerFn = useCallback((feature, latlng) => {
    return L.circleMarker(latlng, {
      radius: 5, fillColor: '#6366f1', color: '#fff', weight: 1.5, fillOpacity: 0.85,
    });
  }, []);

  const geoJsonOutput = conversionResult?.geoJson
    ? JSON.stringify(conversionResult.geoJson, null, 2)
    : '';

  return (
    <>
      <SEO
        title="CSV to GeoJSON Converter | Map Visualizer | UmbrellaCo"
        description="Convert spatial CSV and TSV files into GeoJSON format. Auto-detects coordinate columns, headers, and separators. Download mapping data for free."
        keywords="csv to geojson, spatial csv converter, map csv points, online gis tools"
        url="/spatial-tools/csv-to-geojson"
        jsonLd={createSoftwareAppSchema(
          'CSV to GeoJSON Converter',
          'Convert spatial CSV data into valid GeoJSON map files instantly.',
          'https://umbrellaco.in/spatial-tools/csv-to-geojson',
          ['Spatial Data Conversion', 'GIS Formats', 'Map Data Mapping']
        )}
      />
      <ToolPageContainer
        title="CSV to GeoJSON Map Converter"
        description="Upload any CSV, TSV, or plain text coordinate list. The tool automatically detects separators, assigns spatial columns, and visualizes the result."
        disablePadding
      >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <PanelGroup direction="horizontal">
        {/* Left Panel */}
        <Panel defaultSize={40} minSize={25}>
          <Box sx={{
            height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto',
            bgcolor: 'background.paper', borderRight: `1px solid ${theme.palette.divider}`,
          }}>
            {/* Upload / Paste */}
            <Box sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                <Button
                  component="label" variant="contained" size="small"
                  startIcon={<UploadFileIcon />}
                  sx={{
                    flex: 1,
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' },
                  }}
                >
                  Upload CSV
                  <input type="file" hidden accept=".csv,.tsv,.txt" onChange={handleFileUpload} />
                </Button>
                <Button variant="outlined" size="small" startIcon={<ContentPasteIcon />}
                  onClick={() => setShowPaste(!showPaste)}>
                  Paste
                </Button>
              </Stack>

              {showPaste && (
                <TextField
                  multiline rows={6} fullWidth size="small"
                  placeholder={"Paste CSV/TSV data here...\n\nlat,lon,speed,time\n40.7128,-74.0060,45,2024-01-15T10:30:00Z\n40.7580,-73.9855,32,2024-01-15T10:31:00Z\n\nAlso works with:\n- Tab-separated values\n- Semicolon-separated values\n- Space-separated values\n- No headers (auto-detected)"}
                  value={csvText}
                  onChange={(e) => { setCsvText(e.target.value); setColOverrides({}); }}
                  sx={{ '& textarea': { fontFamily: 'monospace', fontSize: '0.75rem' } }}
                />
              )}

              {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
            </Box>

            <Divider />

            {/* Detection Summary */}
            {parseResult && (
              <Box sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
                  <AutoFixHighIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  <Typography variant="subtitle2" fontWeight={700}>Auto-Detected</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                  <Chip size="small" label={`Separator: ${parseResult.separatorName}`} color="primary" variant="outlined" />
                  <Chip size="small" label={`${parseResult.totalRows} rows`} variant="outlined" />
                  <Chip size="small" label={`${parseResult.colCount} columns`} variant="outlined" />
                  <Chip size="small" label={parseResult.hasHeaderRow ? 'Has headers' : 'No headers'} variant="outlined" />
                </Stack>

                {/* Column Assignment Table */}
                <Typography variant="caption" fontWeight={700} sx={{ mb: 0.5, display: 'block' }}>
                  Column Assignments (click to change)
                </Typography>
                <TableContainer sx={{ maxHeight: '20vh' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5 }}>Header</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5 }}>Role</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5 }}>Sample</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parseResult.headers.map((header, idx) => {
                        const role = getColRole(idx);
                        return (
                          <TableRow key={idx} sx={{ '& td': { py: 0.3, fontSize: '0.7rem' } }}>
                            <TableCell>{idx}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{header}</TableCell>
                            <TableCell>
                              <FormControl size="small" variant="standard" sx={{ minWidth: 80 }}>
                                <Select
                                  value={role || ''}
                                  onChange={(e) => handleColRoleChange(idx, e.target.value || null)}
                                  displayEmpty
                                  sx={{ fontSize: '0.7rem' }}
                                >
                                  <MenuItem value="">
                                    <em>—</em>
                                  </MenuItem>
                                  <MenuItem value="lat">Latitude</MenuItem>
                                  <MenuItem value="lon">Longitude</MenuItem>
                                  <MenuItem value="time">Timestamp</MenuItem>
                                  <MenuItem value="speed">Speed</MenuItem>
                                </Select>
                              </FormControl>
                              {role && (
                                <Chip
                                  label={ROLE_LABELS[role]}
                                  size="small"
                                  sx={{
                                    ml: 0.5, height: 18, fontSize: '0.6rem', fontWeight: 700,
                                    bgcolor: ROLE_COLORS[role] + '22',
                                    color: ROLE_COLORS[role],
                                  }}
                                />
                              )}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                              {parseResult.sampleRows[0]?.[idx] ?? '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            <Divider />

            {/* Geometry Type */}
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Output Geometry</Typography>
              <ToggleButtonGroup
                value={geometryType} exclusive
                onChange={(e, v) => { if (v) setGeometryType(v); }}
                size="small" fullWidth
              >
                <ToggleButton value="points">Points</ToggleButton>
                <ToggleButton value="linestring">LineString</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Divider />

            {/* Result Stats */}
            {conversionResult?.geoJson && (
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Result</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Features</Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {conversionResult.geoJson.features.length}
                    </Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Type</Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {geometryType === 'points' ? 'Points' : 'LineString'}
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            )}

            {/* Export */}
            {conversionResult?.geoJson && (
              <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}`, mt: 'auto', flexShrink: 0 }}>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained" size="small" fullWidth
                      startIcon={<DownloadIcon />}
                      onClick={() => exportGeoJSON(conversionResult.geoJson, 'geojson', 'converted')}
                      sx={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' },
                      }}
                    >
                      Download .geojson
                    </Button>
                    <Tooltip title="Copy GeoJSON">
                      <IconButton size="small"
                        sx={{ border: `1px solid ${theme.palette.divider}` }}
                        onClick={() => handleCopy(geoJsonOutput)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  {copyFeedback && <Chip size="small" label={copyFeedback} color="success" />}
                  <Stack direction="row" spacing={1}>
                    <Button variant="outlined" size="small" fullWidth startIcon={<DownloadIcon />}
                      onClick={() => exportGeoJSON(conversionResult.geoJson, 'kml', 'converted')}>
                      .kml
                    </Button>
                    <Button variant="outlined" size="small" fullWidth startIcon={<DownloadIcon />}
                      onClick={() => exportGeoJSON(conversionResult.geoJson, 'gpx', 'converted')}>
                      .gpx
                    </Button>
                  </Stack>
                  <Button 
                    variant="contained" 
                    color="secondary" 
                    size="small" 
                    fullWidth 
                    startIcon={<SendIcon />}
                    onClick={() => {
                      navigate('/api-tools/api-client', { state: { requestBody: geoJsonOutput } });
                    }}
                  >
                    Send to API Client
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>
        </Panel>

        <PanelResizeHandle style={{ width: '5px', background: theme.palette.divider, cursor: 'col-resize' }} />

        {/* Right Panel - Map */}
        <Panel defaultSize={60} minSize={35}>
          <Box sx={{ height: '100%', width: '100%' }}>
            {conversionResult?.geoJson ? (
              <MapView
                mapId="csv-geojson-map"
                geoJsonData={conversionResult.geoJson}
                pointToLayerFn={geometryType === 'points' ? pointToLayerFn : undefined}
              />
            ) : (
              <Box sx={{
                height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
              }}>
                <Stack alignItems="center" spacing={2} sx={{ textAlign: 'center', p: 4 }}>
                  <TableChartIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                  <Typography variant="h6" color="text.secondary">Upload or paste CSV data</Typography>
                  <Typography variant="body2" color="text.disabled">
                    Auto-detects separators (comma, tab, semicolon, pipe, space)
                    <br />
                    Auto-detects lat, lon, speed, time, heading columns
                  </Typography>
                </Stack>
              </Box>
            )}
          </Box>
        </Panel>
      </PanelGroup>
      </Box>
      </ToolPageContainer>
    </>
  );
}

export default CsvToGeoJson;
